import * as dateUtils from './dateUtils';

/**
 * Localization Utilities
 *
 * Helps to solve problems when working with different countries and locales.
 * Makes it easier when validating, formatting, getting date values.
 *
 */

/**
 * The list of all supported localized countries and their settings.
 * DOBPattern, occupations and nationalIds can also be passed as props to component and props will take priority
 */
const countryInfo = {
	US: {
		showDOB: false,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'MM/DD/YYYY', // DOBPattern can also be passed as props to component
		occupations: [],
		showGuestTermsText: true,
		showGuestTermsCheckBox: false,
		showGuestCPF: false
	},
	CA: {
		showDOB: true,
		showOccupation: true,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: true,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'MM/DD/YYYY',
		showGuestTermsText: true,
		showGuestTermsCheckBox: false,
		showPhoneCountryCode: false,
		occupations: [ // occupations can also be passed as props to component
			{ label: 'Accounting', value: 'Accounting' },
			{ label: 'Administration', value: 'Administration' },
			{ label: 'Arts, Culture', value: 'Arts, Culture' },
			{ label: 'Business', value: 'Business' },
			{ label: 'Communications', value: 'Communications' },
			{ label: 'Customer Service', value: 'Customer Service' },
			{ label: 'Education', value: 'Education' },
			{ label: 'Energy, Utilities', value: 'Energy, Utilities' },
			{ label: 'Engineering', value: 'Engineering' },
			{ label: 'Finance', value: 'Finance' },
			{ label: 'Financial Services', value: 'Financial Services' },
			{ label: 'Government', value: 'Government' },
			{ label: 'Health', value: 'Health' },
			{ label: 'Hospitality', value: 'Hospitality' },
			{ label: 'Human Resources', value: 'Human Resources' },
			{ label: 'Internet', value: 'Internet' },
			{ label: 'Legal', value: 'Legal' },
			{ label: 'Manufacturing', value: 'Manufacturing' },
			{ label: 'Marketing', value: 'Marketing' },
			{ label: 'Non-profit', value: 'Non-profit' },
			{ label: 'Recreation', value: 'Recreation' },
			{ label: 'Religion', value: 'Religion' },
			{ label: 'Research', value: 'Research' },
			{ label: 'Sales', value: 'Sales' },
			{ label: 'Sports, Fitness', value: 'Sports, Fitness' },
			{ label: 'Student', value: 'Student' },
			{ label: 'Technology', value: 'Technology' },
			{ label: 'Trade', value: 'Trade' },
			{ label: 'Transportation', value: 'Transportation' },
			{ label: 'Travel', value: 'Travel' },
		],
		showGuestCPF: false
	},
	GB: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: true,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	AU: {
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: false,
		showLegalTextOnly: true,
		showPasswordHint: true,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	FR: { // FRANCE
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	IT: { // ITALY
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	IL: { // ISRAEL
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showGuestCPF: false
	},
	ES: { // SPAIN
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	IE: { // IRELAND
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: false,
		showPhoneCountryCode: false,
		showGuestCPF: false
	},
	NL: { // NETHERLANDS
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: false
	},
	AT: { // AUSTRIA
		showDOB: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD.MM.YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: false
	},
	CH: { // SWITZERLAND
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: true,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: true,
		consentCheckBoxEnabled: true,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: true,
		showNationalId: true,
		nationalIds: [ // nationalIds can also be passed as props to component
			{ label: 'Identity card', value: 'NATIONAL_ID' },
			{ label: 'Passport', value: 'PASSPORT_NUMBER' },
			{ label: 'Driver licence', value: 'DRIVERS_LICENSE' }
		],
		showGuestCPF: false
	},
	BR: { // BRAZIL
		showDOB: true,
		showDOBHelp: true,
		showDOBMust18: true,
		showOccupation: false,
		showNationality: false,
		showLegalCheckBox: true,
		showLegalTextOnly: false,
		showPasswordHint: false,
		showConsentCheckBox: false,
		consentCheckBoxEnabled: false,
		DOBPattern: 'DD/MM/YYYY',
		occupations: [],
		showGuestTermsText: false,
		showGuestTermsCheckBox: true,
		showPhoneCountryCode: true,
		showGuestCPF: true
	}
};

/**
 * Returns a valid settings for specific country.
 *
 * @param {string} country code.
 * @return {Object} country settings.
 */
export function getCountryInfo(country = 'US') {
	return countryInfo[country] ? countryInfo[country] : countryInfo.US;
}

/**
 * Returns a date of birth string depending on the country format.
 *
 * @param {string} date of birth
 * @param {string} country code
 * @return {string} birthDate
 */
export function getFormattedDob(dob, DOBPattern) {
	return dateUtils.getFormattedDob(dob, DOBPattern);
}

/**
 * Validates date of birth from DoB string.
 *
 * @param {string} date of birth
 * @return {boolean} is valid
 */
export function validateDob(dob, DOBPattern) {
	return dateUtils.validateDob(dob, DOBPattern);
}


/**
 * Validates date of birth must be 18 Years from DoB string.
 *
 * @param {string} date of birth
 * @return {boolean} is valid
 */
export function validateDobIs18(dob, DOBPattern) {
	return dateUtils.isOfLegalAge(dob, DOBPattern);
}
